import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';

/**
 * Phase 17: Step 1 - Role Confirmation
 * Display user's role and explain responsibilities
 */
function Step1Role() {
  const navigate = useNavigate();
  const { familyUser, isAdmin } = useFamilyAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (familyUser) {
      setLoading(false);
    }
  }, [familyUser]);

  if (loading) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="animate-spin w-16 h-16 border-4 border-white/30 border-t-white rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto animate-fade-in">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white/50"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
        </div>
      </div>

      {/* Card */}
      <div className="bg-neutral-900/90 backdrop-blur-xl rounded-3xl border border-neutral-600/40 p-8 sm:p-12 shadow-2xl animate-scale-in">
        {/* Role Badge */}
        <div className="mb-8 text-center">
          <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-2xl font-bold shadow-hayat-lg ${
            isAdmin 
              ? 'bg-gradient-to-r from-hayat-success-500 to-hayat-success-600 text-white' 
              : 'bg-hayat-neutral-100 text-[#CBD5F5]'
          }`}>
            {isAdmin ? '🔑' : '👤'}
            <span>{isAdmin ? 'Administrator' : 'Mitglied'}</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#F9FAFB] mb-4 text-center">
          {isAdmin ? 'Sie sind Administrator' : 'Sie sind Mitglied'}
        </h1>

        {/* Description */}
        <div className="text-left space-y-4 mb-8 text-[#CBD5F5]">
          {isAdmin ? (
            <>
              <p className="text-lg">
                Als <strong>Administrator</strong> haben Sie die volle Kontrolle über den Haushalt:
              </p>
              <ul className="space-y-2 pl-6">
                <li className="flex items-start gap-2">
                  <span className="text-hayat-success-600 font-bold">✓</span>
                  <span>Neue Familienmitglieder einladen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-success-600 font-bold">✓</span>
                  <span>Senioren-Profile verwalten</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-success-600 font-bold">✓</span>
                  <span>Erinnerungen erstellen und bearbeiten</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-success-600 font-bold">✓</span>
                  <span>Geräte verbinden und verwalten</span>
                </li>
              </ul>
            </>
          ) : (
            <>
              <p className="text-lg">
                Als <strong>Mitglied</strong> können Sie:
              </p>
              <ul className="space-y-2 pl-6">
                <li className="flex items-start gap-2">
                  <span className="text-hayat-primary-600 font-bold">✓</span>
                  <span>Senioren-Profile ansehen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-primary-600 font-bold">✓</span>
                  <span>Aktivitäten einsehen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-primary-600 font-bold">✓</span>
                  <span>Benachrichtigungen erhalten</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-hayat-primary-600 font-bold">✓</span>
                  <span>Video-Anrufe tätigen</span>
                </li>
              </ul>
            </>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate(isAdmin ? '/onboarding/step2' : '/onboarding/step3')}
          className="w-full py-5 bg-gradient-to-r from-hayat-primary-500 to-hayat-primary-600 text-white rounded-2xl font-bold text-lg shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300"
        >
          Weiter →
        </button>
      </div>
    </div>
  );
}

export default Step1Role;